import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "../../app/store";
import { getBackendUrl, LoadStateInfo, loaded, loading, notStarted, error, failedResponse, LoadState } from "../common";
import { getToken } from "../../features/user/UserAvatar";
import { SurveyQuestionAnswerInfo } from "./surveyQuestionAnswerListSlice";

export interface SurveyQuestionAnswerState {
    state: LoadStateInfo;
    request?: RetrieveSurveyQuestionAnswerRequest;
    selected?: SurveyQuestionAnswerInfo;
}

const initialState: SurveyQuestionAnswerState = {
    state: notStarted(),
};

export interface RetrieveSurveyQuestionAnswerRequest {
    answerId: string;
}

export const retrieveSurveyQuestionAnswer = createAsyncThunk(
    "retrieveSurveyQuestionAnswer",
    async (param: RetrieveSurveyQuestionAnswerRequest, thunkAPI) => {
        console.debug("retrieve survey question answer");

        const response = await fetch(`${getBackendUrl()}/api/survey/question/answer/get/${param.answerId}`, {
            signal: thunkAPI.signal,
            headers: {
                "Authorization": `Bearer ${getToken()}`,
            }
        });

        if (response.ok) {
            return await response.json();
        } else {
            return thunkAPI.rejectWithValue(failedResponse(response));
        }
    },
    {
        condition: (param: RetrieveSurveyQuestionAnswerRequest, thunkAPI) => {
            const state = thunkAPI.getState() as RootState;
            const status = state.surveyQuestionAnswer;
            if (!status || !status.state) {
                return true;
            }
            if (status.state.state == LoadState.NotStarted) {
                return true;
            }
            console.debug(`retrieveSurveyQuestionAnswer skipped: ${status.state.state}`);
            return false;
        }
    }
);

export const surveyQuestionAnswerSlice = createSlice({
    name: "surveyQuestionAnswer",
    initialState,
    reducers: {
        select: (state, action: PayloadAction<SurveyQuestionAnswerInfo | undefined>) => {
            console.trace(`select :: ${action.payload?.id}`);
            state.selected = action.payload;
        },
        check: (state, action: PayloadAction<string | undefined>) => {
            if (state.state.state != LoadState.NotStarted
                && (action.payload == undefined || state.request?.answerId != action.payload)
            ) {
                state.request = undefined;
                state.state = notStarted();
                state.selected = undefined;
            }
        },
        clean: (state) => {
            state.request = undefined;
            state.selected = undefined;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(retrieveSurveyQuestionAnswer.pending, (state, action) => {
                state.request = action.meta.arg;
                state.state = loading();
                state.selected = undefined;
            })
            .addCase(retrieveSurveyQuestionAnswer.fulfilled, (state, action) => {
                console.debug(`${action.type}`);
                state.state = loaded();
                state.selected = action.payload;
            })
            .addCase(retrieveSurveyQuestionAnswer.rejected, (state, action) => {
                state.state = error(action);
            });
    },
});

export const { select, check, clean } = surveyQuestionAnswerSlice.actions;

export const selectSurveyQuestionAnswer = (state: RootState) => state.surveyQuestionAnswer;

export default surveyQuestionAnswerSlice.reducer;
